import React from 'react';

import { cn } from '@/lib/utils';

/** Tiles shown before the "+N" count; LinkedIn draws at most four. */
export const LINKEDIN_GRID_MAX_TILES = 4;

/**
 * How LinkedIn draws a post with several images in the feed: a collage, not a swipe carousel.
 * Two images sit side by side; three are one tall image beside two stacked; four are a 2x2; with more than four
 * the fourth tile carries a "+N" count of the rest. Tapping opens a viewer that swipes, which the preview does not
 * show. Source: consistent third-party descriptions (Planable, AuthoredUp, SocialRails; read 2026-11-03), no official
 * LinkedIn page describes it, and AuthoredUp notes LinkedIn is experimenting with the layout. Live check in the
 * 2026-11-04 run note.
 */
export const LinkedInImageGrid: React.FC<{
  urls: string[];
  activeIndex: number;
  onSelect?: (index: number) => void;
  focalX?: number;
  focalY?: number;
  alt: string;
  borderColor: string;
}> = ({ urls, activeIndex, onSelect, focalX, focalY, alt, borderColor }) => {
  const shown = urls.slice(0, LINKEDIN_GRID_MAX_TILES);
  const hidden = urls.length - shown.length;
  const count = shown.length;
  const position =
    focalX != null && focalY != null ? `${focalX * 100}% ${focalY * 100}%` : '50% 50%';

  return (
    <div
      className={cn(
        'grid w-full grid-cols-2 gap-0.5 overflow-hidden',
        count === 2 ? 'aspect-[2/1] grid-rows-1' : 'aspect-square grid-rows-2',
      )}
      data-linkedin-image-grid={count}
      style={{ backgroundColor: borderColor }}
    >
      {shown.map((url, index) => {
        const isLastWithMore = index === shown.length - 1 && hidden > 0;
        const active = index === activeIndex;
        return (
          <button
            type="button"
            key={`${url}-${index}`}
            onClick={() => onSelect?.(index)}
            aria-label={`Image ${index + 1} of ${urls.length}`}
            aria-current={active ? 'true' : undefined}
            className={cn(
              'relative block min-h-0 overflow-hidden bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              count === 3 && index === 0 ? 'row-span-2' : null,
              active && urls.length > 1 ? 'ring-2 ring-inset ring-ring' : null,
            )}
          >
            <img
              src={url}
              alt={index === 0 ? alt : ''}
              loading="lazy"
              draggable={false}
              className="absolute inset-0 size-full object-cover"
              style={{ objectPosition: active ? position : '50% 50%' }}
            />
            {isLastWithMore ? (
              // eslint-disable-next-line no-restricted-syntax -- text on a photo scrim stays white in both themes
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-2xl font-semibold text-white">
                +{hidden}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};
